<template>
  <div class="mt-1 mb-2 justify-content-center">
    <div
      v-if="!isOpen"
      class="input-wrapper"
      :class="{ fill, error, disabled }"
    >
      <div class="placeholder-container" @click="onClick">
        <div v-if="value === ''" class="placeholder-text">
          {{ placeholder }}
        </div>
        <div v-else class="selected-option">{{ selectedValue }}</div>
        <div class="icon-wrapper">
          <i class="fas fa-chevron-down" />
        </div>
      </div>
    </div>
    <div v-if="isOpen" class="options-container h-auto">
      <div class="placeholder-container" @click="handleOptionSelected('')">
        <div class="placeholder-text">{{ placeholder }}</div>
        <div class="icon-wrapper">
          <i class="fas fa-chevron-up" />
        </div>
      </div>
      <div
        class="option h-100"
        v-for="option in options"
        v-bind:key="option"
        @click="handleOptionSelected(option.key)"
      >
        {{ option.text }}
      </div>
    </div>
    <div
      v-if="isOther"
      class="text-input-wrapper mt-2"
      :class="{ fill, error, disabled }"
    >
      <input
        class="input-element"
        :style="inputStyle"
        :type="textInputType"
        :value="otherValue"
        @input="($event) => handleOtherInput($event.target.value)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, watchEffect } from "vue";

type SelectInputOption = {
  key: string;
  text: string;
};

interface Props {
  options: SelectInputOption[];
  value: string;
  placeholder: string;
  fill: boolean;
  disabled: boolean;
  expand: boolean;
  keyForOtherOption: string;
  textInputType: string;
}

export default defineComponent({
  name: "SelectInpuntExpandable",
  props: {
    options: {
      type: Array as PropType<SelectInputOption[]>,
      default() {
        return [];
      },
    },
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    fill: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    expand: {
      type: Boolean,
      default: false,
    },
    keyForOtherOption: {
      type: String,
      default: "OTHER",
    },
    inputStyle: Object,
    otherValue: {
      type: String,
      default: "",
    },
    textInputType: {
      type: String,
      default: "text",
    },
  },
  emits: ["update:value", "update:otherValue"],
  setup(props: Props) {
    const isOpen = ref<boolean>(false);
    const isOther = ref<boolean>(false);
    const selectedValue = ref<string>("");
    //const otherValue = ref<string>("");
    const selectedKey = ref<string>();

    watchEffect(() => {
      let selected = props?.options?.find((e) => e.key === props.value)?.text;
      selectedValue.value = selected !== undefined ? selected : "";
    });

    return {
      isOpen,
      isOther,
      selectedValue,
      selectedKey,
      //otherValue,
    };
  },
  methods: {
    handleOptionSelected(newValue: string) {
      this.$emit("update:value", newValue);
      this.isOther = this.keyForOtherOption === newValue ? true : false;
      this.isOpen = false;
    },
    handleOtherInput(newValue: string) {
      this.$emit("update:otherValue", newValue);
    },
    onClick() {
      this.isOpen = true;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.input-wrapper {
  height: 50px;
  background-color: white;
  border: 1px solid $pj-color-light-gray;
  border-radius: 11px;
  padding: 5px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;

  &:focus-within {
    border: 1px solid $pj-color-black;
  }

  &.error {
    border: 1px solid $pj-color-red;
  }

  &.fill {
    width: 100%;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
.placeholder-container {
  height: 50px;
  display: flex;
  align-items: center;
  width: 100%;
}
.placeholder-text {
  color: $pj-color-cool-gray;
  font-size: 16px;
  border: none;
  flex-grow: 1;
  background-color: transparent;
}
.selected-option {
  flex-grow: 1;
  color: $pj-color-black;
  font-size: 16px;
}
.icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-black;
  width: 20px;
  height: 20px;
  font-size: 14px;

  i {
    margin: 0;
  }
}
.options-container {
  left: 0;
  width: 100%;
  min-height: 50px;
  overflow-y: scroll;
  background-color: white;
  border: 1px solid $pj-color-black;
  border-radius: 11px;
  padding: 0 20px;
  z-index: 100;
}
.option {
  padding: 5px 0;
  color: $pj-color-black;
  font-size: 16px;
}
.text-input-wrapper {
  height: 50px;
  background-color: white;
  border: 1px solid $pj-color-light-gray;
  border-radius: 11px;
  padding: 5px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;

  &:focus-within {
    border: 1px solid $pj-color-black;
  }

  &.fill {
    width: 100%;
  }

  &.error {
    border: 1px solid $pj-color-red;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
.input-element {
  color: $pj-color-black;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  border: none;
  flex-grow: 1;
  height: 100%;
  width: 100%;

  &:focus-visible,
  &:focus,
  &:active {
    outline-width: 0;
  }

  &::placeholder {
    color: $pj-color-cool-gray;
  }
}
</style>
